// AI-powered template and configuration generation service
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Template Generation Types
export interface AITemplateGenerateRequest {
  description: string;
  vendor?: string;
  switch_type?: string;
  template_name?: string;
  save_to_database?: boolean;
}

export interface AITemplateGenerateResponse {
  template_id?: string;
  template_content: string;
  parameter_schema: Record<string, any>;
  supported_features: Record<string, boolean>;
  confidence_score: number;
  missing_parameters: string[];
  suggestions: string[];
  saved_to_database: boolean;
}

export interface AITemplateEnhanceRequest {
  enhancement_description: string;
  update_template?: boolean;
}

export interface AITemplateEnhanceResponse {
  template_id: string;
  original_content: string;
  enhanced_content: string;
  improvements: string[];
  confidence_score: number;
  suggestions: string[];
  template_updated: boolean;
}

// Configuration Generation Types
export interface AIConfigGenerateRequest {
  description: string;
  vendor?: string;
  switch_type?: string;
  template_id?: string | null;
  parameters?: Record<string, any>;
  validate?: boolean;
  save_to_database?: boolean;
}

export interface AIConfigGenerateResponse {
  configuration_id?: string;
  configuration_text: string;
  parameters_extracted: Record<string, any>;
  template_used: string;
  validation_status: string;
  validation_errors: string[];
  validation_warnings: string[];
  suggestions: string[];
  confidence_score: number;
  missing_parameters: string[];
  saved_to_database: boolean;
}

export interface AIBatchConfigRequest {
  batch_description: string;
  vendor?: string;
  switch_type?: string;
  template_id?: string | null;
  save_to_database?: boolean;
}

export interface AIBatchConfigResponse {
  configurations: Array<{
    configuration_id?: string;
    switch_name: string;
    configuration_text: string;
    validation_status: string;
  }>;
  total_generated: number;
  batch_parameters: Record<string, any>;
  saved_to_database: boolean;
}

export interface ConfigSuggestion {
  parameter: string;
  suggested_value: string;
  description: string;
  confidence?: number;
}

export interface AIAnalysisResult {
  security_score: number;
  completeness_score: number;
  best_practices_score: number;
  issues: string[];
  warnings: string[];
  suggestions: string[];
  extracted_features: Record<string, boolean>;
}

class AIService {
  private getAuthHeader() {
    // Get token from localStorage or session storage if available
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  // Template Generation Methods
  async generateTemplate(request: AITemplateGenerateRequest): Promise<AITemplateGenerateResponse> {
    const response = await axios.post(`${API_BASE_URL}/api/templates/ai-generate`, request, {
      headers: this.getAuthHeader()
    });
    return response.data;
  }

  async enhanceTemplate(
    templateId: string,
    request: AITemplateEnhanceRequest
  ): Promise<AITemplateEnhanceResponse> {
    const response = await axios.post(`${API_BASE_URL}/api/templates/ai-enhance/${templateId}`, request, {
      headers: this.getAuthHeader()
    });
    return response.data;
  }

  async analyzeTemplate(templateId: string): Promise<AIAnalysisResult> {
    const response = await axios.get(`${API_BASE_URL}/api/templates/ai-analyze/${templateId}`, {
      headers: this.getAuthHeader()
    });
    return response.data.analysis;
  }

  // Configuration Generation Methods
  async generateConfiguration(request: AIConfigGenerateRequest): Promise<AIConfigGenerateResponse> {
    const response = await axios.post(`${API_BASE_URL}/api/configurations/ai-generate`, request, {
      headers: this.getAuthHeader()
    });
    return response.data;
  }

  async generateBatchConfigurations(request: AIBatchConfigRequest): Promise<AIBatchConfigResponse> {
    const response = await axios.post(`${API_BASE_URL}/api/configurations/ai-batch-generate`, request, {
      headers: this.getAuthHeader()
    });
    return response.data;
  }

  async getConfigurationSuggestions(
    switchType: string,
    vendor: string = 'extreme',
    partialDescription?: string
  ): Promise<ConfigSuggestion[]> {
    const params = new URLSearchParams({
      vendor,
      ...(partialDescription && { partial_description: partialDescription })
    });
    const response = await axios.get(`${API_BASE_URL}/api/configurations/ai-suggest/${switchType}?${params}`, {
      headers: this.getAuthHeader()
    });
    return response.data;
  }

  async validateConfiguration(
    configurationText: string,
    switchType: string,
    vendor: string = 'extreme'
  ): Promise<{
    is_valid: boolean;
    errors: string[];
    warnings: string[];
    suggestions: string[];
    extracted_parameters: Record<string, any>;
    confidence_score: number;
  }> {
    const response = await axios.post(`${API_BASE_URL}/api/configurations/ai-validate`, {
      configuration_text: configurationText,
      switch_type: switchType,
      vendor
    }, {
      headers: this.getAuthHeader()
    });
    return response.data;
  }

  // Helper Methods
  getSampleDescriptions(type: 'template' | 'configuration'): string[] {
    if (type === 'template') {
      return [
        'Create an edge switch template for 48 ports with standard VLANs',
        'Generate a core switch configuration with VRRP for high availability',
        'Build a distribution switch template with QoS and custom VLANs',
        'Design a template for campus edge switches with 802.1X authentication'
      ];
    } else {
      return [
        'Configure edge switch SW-FLOOR2-01 at Building A with IP 192.168.1.10',
        'Set up core switch CORE-01 with VRRP for high availability, primary role',
        'Create config for 48-port switch with custom VLAN Guest on tag 100',
        'Configure 5 edge switches for Building A floors 1-5, starting IP 192.168.1.10'
      ];
    }
  }

  getVendorOptions(): Array<{ value: string; label: string }> {
    return [
      { value: 'extreme', label: 'Extreme Networks' },
      { value: 'cisco', label: 'Cisco' }
    ];
  }

  getSwitchTypeOptions(): Array<{ value: string; label: string }> {
    return [
      { value: 'edge', label: 'Edge Switch' },
      { value: 'core', label: 'Core Switch' },
      { value: 'distribution', label: 'Distribution Switch' }
    ];
  }

  // Validation Helpers
  validateDescription(description: string): { isValid: boolean; message?: string } {
    if (!description || description.trim().length < 10) {
      return {
        isValid: false,
        message: 'Description must be at least 10 characters long'
      };
    }

    if (description.length > 1000) {
      return {
        isValid: false,
        message: 'Description must be less than 1000 characters'
      };
    }

    return { isValid: true };
  }

  // Score Interpretation
  interpretConfidenceScore(score: number): {
    label: string;
    color: 'error' | 'warning' | 'success';
    description: string;
  } {
    if (score >= 0.8) {
      return {
        label: 'High Confidence',
        color: 'success',
        description: 'AI is highly confident in the generated output'
      };
    } else if (score >= 0.6) {
      return {
        label: 'Medium Confidence',
        color: 'warning',
        description: 'AI has moderate confidence, review the output carefully'
      };
    } else {
      return {
        label: 'Low Confidence',
        color: 'error',
        description: 'AI has low confidence, significant review required'
      };
    }
  }

  interpretSecurityScore(score: number): {
    label: string;
    color: 'error' | 'warning' | 'success';
  } {
    if (score >= 85) {
      return { label: 'Excellent Security', color: 'success' };
    } else if (score >= 70) {
      return { label: 'Good Security', color: 'warning' };
    } else {
      return { label: 'Security Issues', color: 'error' };
    }
  }
}

const aiService = new AIService();
export default aiService;